import { Button } from 'entities/Button'
import { CheckBox } from 'entities/ChekBox'
import { resetCache } from './resetCache'
import { useBranchStore } from 'shared/store/branchesSlice'
import { useGlobalStore } from 'shared/store/globalSlice'
import { FormLabel } from '@mui/material'
import { Disclaimer, ResetCacheContainer } from './styled'
import { useEffect, useRef, useState } from 'react'
import { ConfirmationModal } from 'entities/ConfirmationModal'
import { GET } from 'shared/api/GET'
import { POST } from 'shared/api/POST'
import { CacheHasBeenResetToaster } from './CacheHasBeenResetToaster'
import { resetAllCaches } from './resetAllCaches'

export const ResetCacheHandler = () => {
  // Store
  const isCC = useGlobalStore().isCCSelected
  const branchId = useBranchStore().branchId
  const branchList = useBranchStore().branchList
  const selectedBranch = branchList?.find(item => +item.Intern === branchId)?.[
    isCC ? 'ClickAndCollectClientId' : 'QRMenuClientId'
  ]
  const registerId = selectedBranch || 0

  // State
  const [registerOfResettingCache, setRegisterOfResettingCache] = useState<number | 'all' | null>(
    null
  )
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<false | 1 | 2>(false)
  const [isCacheResetToasterOpen, setIsCacheResetToasterOpen] = useState(false)
  const [isOldCacheUsed, setIsOldCacheUsed] = useState<boolean>(false)

  // Constants
  const timerIdRef = useRef<number | null>(null)
  const resetCacheText = 'Reset cache'
  const resetAllCachesText = 'Reset all caches'
  const cacheREsetInProgressText = 'Cache reset in progress...'
  const disclaimer =
    'This action resets the product cache for the selected branch. It usually takes up to 10 minutes to complete.'

  // useEffects
  useEffect(() => {
    void GET.config(0).then(config => {
      setIsOldCacheUsed(config?.isOldCacheUsed === true)
    })
  }, [])

  useEffect(() => {
    if (timerIdRef.current !== null) {
      window.clearInterval(timerIdRef.current)
      timerIdRef.current = null
    }

    if (registerOfResettingCache != null) {
      timerIdRef.current = window.setInterval(() => {
        void checkCacheStatus(registerOfResettingCache)
      }, 10_000)
    }

    return () => {
      if (timerIdRef.current !== null) {
        window.clearInterval(timerIdRef.current)
        timerIdRef.current = null
      }
    }
  }, [registerOfResettingCache])

  useEffect(() => {
    if (registerId) {
      void checkCacheStatus(registerId)
    }
  }, [registerId])

  // Functions
  const checkCacheStatus = async (id: number | 'all') => {
    if (!id) return
    let res
    if (id === 'all') {
      res = await GET.cacheStatus()
    } else {
      res = await GET.cacheStatus(id)
    }
    if (res?.cacheStatus === 1) {
      setRegisterOfResettingCache(id)
    } else {
      if (!!registerOfResettingCache) {
        setIsCacheResetToasterOpen(true)
        setRegisterOfResettingCache(null)
      }
    }
    return res?.cacheStatus
  }

  const handleToggleNewCache = async (_: boolean) => {
    const next = !isOldCacheUsed
    setIsOldCacheUsed(next)
    await POST.upsertCacheConfig(next)
    await resetAllCaches()
    setRegisterOfResettingCache('all')
  }

  return (
    <>
      <FormLabel>{'Product cache reset'}</FormLabel>
      <ResetCacheContainer>
        <Disclaimer>
          Note: {!!registerOfResettingCache ? cacheREsetInProgressText : disclaimer}
        </Disclaimer>
        <CheckBox
          checked={!isOldCacheUsed}
          handleChange={handleToggleNewCache}
          label='Use new cache'
        />
        <Button
          isLoading={registerOfResettingCache === registerId || !registerId}
          handleClick={() => setIsConfirmationOpen(1)}
          isDisabled={!!registerOfResettingCache}
        >
          {resetCacheText}
        </Button>
        <Button
          isLoading={registerOfResettingCache === 'all' || !registerId}
          handleClick={() => setIsConfirmationOpen(2)}
          isDisabled={!!registerOfResettingCache}
        >
          {resetAllCachesText}
        </Button>
      </ResetCacheContainer>
      <ConfirmationModal
        isOpen={!!isConfirmationOpen}
        onClose={() => setIsConfirmationOpen(false)}
        onCancel={() => setIsConfirmationOpen(false)}
        onConfirm={async () => {
          if (isConfirmationOpen === 1) {
            await resetCache(registerId)
            setIsConfirmationOpen(false)
            setRegisterOfResettingCache(registerId)
          } else if (isConfirmationOpen === 2) {
            await resetAllCaches()
            setIsConfirmationOpen(false)
            setRegisterOfResettingCache('all')
          }
        }}
        message={`${disclaimer} Are you sure you want to reset the cache?`}
        headerTitle={resetCacheText}
      />
      <CacheHasBeenResetToaster
        isOpen={isCacheResetToasterOpen}
        onClose={() => setIsCacheResetToasterOpen(false)}
      />
    </>
  )
}
